import { Component, OnDestroy, OnInit } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { Router } from "@angular/router";
import { RequestListType } from "app/bundles/requests/shared/enums/request-list-type.enum";
import { FilterService } from "app/bundles/requests/shared/services/filter.service";
import { MapService } from "app/bundles/requests/shared/services/map.service";
import { RequestService } from "app/bundles/requests/shared/services/request.service";
import { UtilService } from "app/bundles/requests/shared/services/util.service";
import { CoreService } from "app/core/core.service";
import { StorageService } from "app/shared/services/storage.service";
import * as _ from "lodash-es";
import { Subscription, forkJoin as observableForkJoin } from "rxjs";
import { debounceTime, delay, tap } from "rxjs/operators";
import { Feature } from "../shared/models/feature";

@Component({
  selector: "app-request-menu",
  templateUrl: "./request-menu.component.html",
  styleUrls: ["./request-menu.component.scss"],
})
export class RequestMenuComponent implements OnInit, OnDestroy {
  private listType: number = RequestListType.BOTH;
  public sharedButtonClass: any;
  public listOnlyButtonClass: any;
  public mapOnlyButtonClass: any;
  public requestListType = RequestListType;
  public searchForm: UntypedFormGroup;
  private subscriptions = new Subscription();
  private _modal = null;
  public alertsCount = 0;
  public isSearchTextEmpty = true;
  public enableModal = false;

  constructor(
    private fb: UntypedFormBuilder,
    private coreService: CoreService,
    private router: Router,
    private filterService: FilterService,
    private storageService: StorageService,
    private utilService: UtilService,
    private requestService: RequestService,
    private mapService: MapService,
  ) {}

  ngOnInit() {
    const storageFilters = this.storageService.getFilters();
    if (storageFilters != null) {
      this.listType = _.get(storageFilters, "filters", {
        ...storageFilters,
        listType: RequestListType.BOTH,
      });
      this.updateFilter(storageFilters.listType);
    }

    this.updateListTypeClass();
    this.centerOnMarkers();
    this.initSearchForm(storageFilters.searchTerm);

    const features: Feature[] = this.filterService.differenceAlerts(
      this.storageService.getLastGeoJsonRequest(),
      this.storageService.getGeoJsonRequest(),
    );
    if (features) {
      this.alertsCount = features.length;
    }

    this.subscriptions.add(
      this.coreService.refreshIcons$.pipe(delay(2000)).subscribe(() => {
        this.mapService.verifyMap();
      }),
    );

    this.subscriptions.add(
      this.coreService.resetSearchBox$.subscribe(() => this.onResetSearch()),
    );

    this.searchForm.valueChanges.pipe(debounceTime(300)).subscribe((form) => {
      this.storageService.updateFilters("searchTerm", form.searchRequest);
      this.isSearchTextEmpty = form.searchRequest ? false : true;
      this.filter();
      this.coreService.notifySearchFilterParameter({
        option: "searchFilterParameter",
        value: form.searchRequest,
      });
    });

    if (storageFilters.searchTerm != "") {
      this.isSearchTextEmpty = storageFilters.searchTerm ? false : true;
      this.filter();
      this.coreService.notifySearchFilterParameter({
        option: "searchFilterParameter",
        value: storageFilters.searchTerm,
      });
      this.storageService.updateFilters(
        "searchTerm",
        storageFilters.searchTerm,
      );
    }

    this.centerOnMarkers();
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  bindModal(modal) {
    this._modal = modal;
  }

  filter(): void {
    this.utilService.resetFilter(this.storageService.getGeoJsonRequest());
    this.utilService.resetSelection(this.storageService.getGeoJsonRequest());
    this.utilService.geojsonfilter(
      this.storageService.getGeoJsonRequest(),
      this.storageService.getFilters(),
    );
    this.storageService.notifyComponents();
    this.storageService.notifyMap();
    this.storageService.notifyList();
  }

  initSearchForm(searchRequest: string): void {
    this.searchForm = this.fb.group({
      searchRequest: [searchRequest],
    });
  }

  onClickBtn(event) {
    this.listType = event;
    this.storageService.updateFilters("listType", event);
    this.coreService.notifyListType(this.listType);
    if (event === 2) {
      this.coreService.notifyRequestUpdate([]);
    }
    this.updateListTypeClass();
    if (event != RequestListType.LIST) {
      this.mapService.saveMapStatus();
    }
  }

  updateFilter(event) {
    this.listType = event;
    this.storageService.updateFilters("listType", event);
    this.coreService.notifyListType(this.listType);
    if (event === 2) {
      this.coreService.notifyRequestUpdate([]);
    }
    this.updateListTypeClass();
  }

  updateListTypeClass(): void {
    this.sharedButtonClass = {
      "btn-act": this.listType === RequestListType.BOTH,
    };
    this.listOnlyButtonClass = {
      "btn-act": this.listType === RequestListType.LIST,
    };
    this.mapOnlyButtonClass = {
      "btn-act": this.listType === RequestListType.MAP,
    };
  }

  onRefresh(): void {
    this.coreService.startSpinner();
    observableForkJoin([
      this.mapService.resetMapStatus(),
      this.requestService.getGeojsonObservable(),
    ])
      .pipe(
        tap(() => {
          // we re apply our existing filters in refreshed GeoJson
          this.utilService.geojsonfilter(
            this.storageService.getGeoJsonRequest(),
            this.storageService.getFilters(),
          );
          this.storageService.notifyComponents();
          this.storageService.notifyMap();
          this.storageService.notifyList();
        }),
      )
      .subscribe((r) => this.coreService.stopSpinner());
  }

  centerOnMarkers() {
    this.coreService.notifyCenterOnMarkers({
      option: "centerOnMarkers",
      value: true,
    });
  }

  onSearchRequest(): void {
    this.filter();
  }

  onResetSearch() {
    this.searchForm.setValue({
      searchRequest: "",
    });
    this.storageService.updateFilters("searchTerm", "");
    this.filter();
  }

  open() {
    this.enableModal = true;
    this._modal.open();
  }

  close() {
    this._modal.close();
  }

  onEnableModal(event) {
    this.enableModal = event;
  }
}
